import { InfoIcon, UserIcon, UsersIcon } from "@phosphor-icons/react/dist/ssr";
import type { MutableRefObject, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import type { PhoneCheckoutStep, UserData } from "../../hooks/use-phone-checkout-flow";
import type { ConfigName } from "../../types/config-name";
import type { FormValues } from "../user-data-form/validation-schema";
import { EditableSummaryCard } from "./editable-summary-card";

// Imperative handle exposed to parents (shared-checkout) so the page-level
// submit CTA can ask "which cards still need data?" — sets the in-card flash
// banners as a side effect and returns the result for the caller to gate on.
export type CardsValidationHandle = {
  checkAndFlash: () => { patientNeeded: boolean; customerNeeded: boolean };
};

interface CardCheckoutLayoutProps {
  configName: ConfigName;
  patientSection: ReactNode;
  customerSection: ReactNode;
  formValues: FormValues;
  hasLabplus?: boolean;
  phoneCheckoutStep?: PhoneCheckoutStep;
  currentPhone?: { dialCode: string; phone: string } | null;
  trigger: UseFormMethods<FormValues>["trigger"];
  bearerRegistrationRequired?: boolean;
  customerResetKey?: number;
  prefilledUserData?: UserData | null;
  isGuestCheckout?: boolean;
  showPasswordFields?: boolean;
  onCancelPatient?: (snapshot: Partial<FormValues> | null) => void;
  onCancelCustomer?: (snapshot: Partial<FormValues> | null) => void;
  activeCard?: "patient" | "customer" | null;
  onActiveCardChange?: (card: "patient" | "customer" | null) => void;
  // Hide the orderer card's edit CTA after the backend committed the customer
  // (POST /registrations success). Patient (recipient) details stay editable —
  // a downstream order rejection on e.g. invalid PESEL is the case the user is
  // expected to resolve there.
  lockCustomerEdit?: boolean;
  // Parent (shared-checkout) writes through this ref to query "are required
  // cards filled?" when the page-level submit CTA is clicked. Calling
  // checkAndFlash also sets the in-card flash banners.
  cardsValidationRef?: MutableRefObject<CardsValidationHandle | null>;
  // Called when the user picks an order type from the patient-card intro.
  // PatientForm wires this to setValue("orderType", ...).
  onSelectOrderType?: (orderType: "for_self" | "for_someone") => void;
}

export const CardCheckoutLayout = ({
  configName,
  patientSection,
  customerSection,
  formValues,
  hasLabplus,
  phoneCheckoutStep: _phoneCheckoutStep,
  currentPhone,
  trigger,
  bearerRegistrationRequired = true,
  customerResetKey,
  prefilledUserData,
  isGuestCheckout = false,
  showPasswordFields = false,
  onCancelPatient,
  onCancelCustomer,
  activeCard,
  onActiveCardChange,
  lockCustomerEdit,
  cardsValidationRef,
  onSelectOrderType,
}: CardCheckoutLayoutProps) => {
  const { t } = useTranslation();
  const [internalActiveCard, setInternalActiveCard] = useState<"patient" | "customer" | null>(null);
  // Set when the page-level submit CTA is clicked while a required card is
  // still empty. Banner clears automatically once the missing snapshot lands
  // (effects below) so users aren't stuck looking at a stale prompt.
  const [validationFlash, setValidationFlash] = useState<{ patient: boolean; customer: boolean }>({
    patient: false,
    customer: false,
  });
  const currentActiveCard = activeCard !== undefined ? activeCard : internalActiveCard;
  const handleOpenCard = (card: "patient" | "customer" | null) => {
    if (onActiveCardChange) {
      onActiveCardChange(card);
    } else {
      setInternalActiveCard(card);
    }
  };
  const [patientSnapshot, setPatientSnapshot] = useState<Partial<FormValues> | null>(null);
  const [customerSnapshot, setCustomerSnapshot] = useState<Partial<FormValues> | null>(null);
  const [customerPhoneSnapshot, setCustomerPhoneSnapshot] = useState<{ dialCode: string; phone: string } | null>(null);

  // Patient-card intro overlay: forces an explicit "for self / for someone"
  // choice on first entry so users don't fill PESEL under the wrong tab. Reset
  // when the card closes — if the user cancelled without saving (no snapshot),
  // they get the intro again on next open.
  const [introDismissed, setIntroDismissed] = useState(false);
  useEffect(() => {
    if (currentActiveCard !== "patient") setIntroDismissed(false);
  }, [currentActiveCard]);
  const showPatientIntro = currentActiveCard === "patient" && !patientSnapshot && !introDismissed;

  const handleSelectOrderType = (orderType: "for_self" | "for_someone") => {
    onSelectOrderType?.(orderType);
    setIntroDismissed(true);
  };

  // Expose snapshots via refs so PatientForm can access them for cancel logic
  const patientSnapshotRef = useRef(patientSnapshot);
  const customerSnapshotRef = useRef(customerSnapshot);
  useEffect(() => {
    patientSnapshotRef.current = patientSnapshot;
  }, [patientSnapshot]);
  useEffect(() => {
    customerSnapshotRef.current = customerSnapshot;
  }, [customerSnapshot]);

  // Always keep a ref to the latest values to avoid stale closure issues in async handlers
  const latestFormValuesRef = useRef(formValues);
  const latestCurrentPhoneRef = useRef(currentPhone);
  useEffect(() => {
    latestFormValuesRef.current = formValues;
  });
  useEffect(() => {
    latestCurrentPhoneRef.current = currentPhone;
  });

  // Reset customer snapshot when phone changes (customerResetKey increments).
  const isFirstRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: customerResetKey is intentionally used as a trigger
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCustomerSnapshot(null);
    setCustomerPhoneSnapshot(null);
    handleOpenCard("customer");
  }, [customerResetKey]);

  const customerFieldNames: (keyof FormValues)[] = [
    "firstName",
    "lastName",
    ...(isGuestCheckout ? [] : (["email", "emailConfirmation"] as const)),
    ...(showPasswordFields && !isGuestCheckout ? (["password", "passwordConfirmation"] as const) : []),
    "dialCode",
    "phone",
    "acceptDataProcessing",
    "acceptTouNPp",
    "acceptNewsletter",
    "acceptMarketingPhoneContact",
  ];

  const handleCompletePatient = async () => {
    const latest = latestFormValuesRef.current;
    const patientFieldNames: (keyof FormValues)[] = [
      "orderType",
      "pesel",
      "noPesel",
      "dateOfBirth",
      "gender",
      ...(latest.orderType === "for_someone"
        ? (["patientFirstName", "patientLastName", "patientPhone", "patientDialCode"] as const)
        : []),
    ];
    const isValid = await trigger(patientFieldNames);
    if (!isValid) return;

    // If bearer is not required and no identity data was provided, treat as a
    // reset: clear the existing snapshot too so editing-then-emptying the card
    // collapses back to the "empty" state instead of showing a stale summary.
    const hasIdentityData = (!latest.noPesel && !!latest.pesel) || (!!latest.noPesel && !!latest.dateOfBirth);
    if (!bearerRegistrationRequired && !hasIdentityData) {
      setPatientSnapshot(null);
      handleOpenCard(null);
      return;
    }

    setPatientSnapshot({ ...latest });
    handleOpenCard(null);
  };

  const handleCompleteCustomer = async () => {
    if (isPhoneAuthInProgress) {
      return;
    }
    const isValid = await trigger(customerFieldNames);
    if (!isValid) return;
    // Use ref to capture latest values after the async trigger call.
    // Merge prefilledUserData so readonly fields (DataLoaded step) are captured correctly
    // even when their form inputs are not registered.
    const latest = latestFormValuesRef.current;
    setCustomerSnapshot({
      ...latest,
      ...(prefilledUserData && {
        firstName: prefilledUserData.firstName,
        lastName: prefilledUserData.lastName,
        email: prefilledUserData.email,
      }),
    });
    setCustomerPhoneSnapshot(latestCurrentPhoneRef.current ?? null);
    handleOpenCard(null);
  };

  const renderPatientSummary = () => {
    if (!patientSnapshot) return null;

    const { orderType, patientFirstName, patientLastName, pesel, noPesel, dateOfBirth, patientPhone, patientDialCode } =
      patientSnapshot;

    const isForSomeone = orderType === "for_someone";
    const hasIdentityData = (!noPesel && !!pesel) || (!!noPesel && !!dateOfBirth);

    return (
      <div className="flex flex-col gap-1 text-sm">
        <div className="font-medium text-foreground">
          {isForSomeone
            ? `${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SOMEONE")}: ${patientFirstName || ""} ${patientLastName || ""}`.trim()
            : t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SELF")}
        </div>
        {hasIdentityData && (
          <div className="text-muted-foreground">
            Dane identyfikacyjne ({noPesel ? "data urodzenia" : "PESEL"}) uzupełnione
          </div>
        )}
        {isForSomeone && patientPhone && (
          <div className="text-muted-foreground">
            Tel: {patientDialCode || "+48"} {patientPhone}
          </div>
        )}
      </div>
    );
  };

  const renderCustomerSummary = () => {
    // Show snapshot data after explicit save, otherwise fall back to live form values
    const dataToUse = customerSnapshot ?? formValues;

    const { firstName, lastName, email } = dataToUse;
    const phone = customerPhoneSnapshot
      ? `${customerPhoneSnapshot.dialCode} ${customerPhoneSnapshot.phone}`
      : currentPhone
        ? `${currentPhone.dialCode} ${currentPhone.phone}`
        : dataToUse.phone;

    if (!firstName && !lastName && !email && !phone) return null;

    return (
      <div className="flex flex-col gap-1 text-sm">
        {(firstName || lastName) && (
          <div className="font-medium text-foreground">
            {firstName} {lastName}
          </div>
        )}
        {email && <div className="text-muted-foreground">{email}</div>}
        {phone && <div className="text-muted-foreground">Tel: {phone}</div>}
      </div>
    );
  };

  const isPhoneAuthInProgress =
    _phoneCheckoutStep !== undefined &&
    _phoneCheckoutStep !== "NewUser" &&
    _phoneCheckoutStep !== "NewUserOTPOverlay" &&
    _phoneCheckoutStep !== "DataLoaded";

  // Once a snapshot lands the corresponding card is "completed" — the flash
  // banner becomes obsolete. Effects are split so saving one card doesn't
  // dismiss a still-relevant flash on the other.
  useEffect(() => {
    if (patientSnapshot) setValidationFlash((s) => (s.patient ? { ...s, patient: false } : s));
  }, [patientSnapshot]);
  useEffect(() => {
    if (customerSnapshot) setValidationFlash((s) => (s.customer ? { ...s, customer: false } : s));
  }, [customerSnapshot]);

  // Wire the imperative handle the parent uses to check "do any required
  // cards still need filling?". We read snapshots through their refs so the
  // closure always sees the latest values without re-binding on every state
  // change.
  useEffect(() => {
    if (!cardsValidationRef) return;
    cardsValidationRef.current = {
      checkAndFlash: () => {
        const patientNeeded = bearerRegistrationRequired && !patientSnapshotRef.current;
        const customerNeeded = !customerSnapshotRef.current;
        setValidationFlash({ patient: patientNeeded, customer: customerNeeded });
        return { patientNeeded, customerNeeded };
      },
    };
    return () => {
      cardsValidationRef.current = null;
    };
  }, [cardsValidationRef, bearerRegistrationRequired]);

  const validationBanner = (
    <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4 text-red-800 text-sm leading-relaxed">
      <InfoIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-red-600" />
      <p>{t("VOUCHERS_DISTRIBUTION.CHECKOUT_CARD.VALIDATION_NEEDED")}</p>
    </div>
  );

  const patientIntroView = (
    <div className="flex flex-col gap-5 p-4 lg:p-6">
      <div>
        <h3 className="font-serif text-2xl">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.ORDER_FOR_PROMPT")}</h3>
        <p className="mt-1 max-w-[720px] text-pretty text-muted-foreground text-sm">
          {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.ORDER_FOR_PROMPT_DESCRIPTION")}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <button
          type="button"
          data-testid="patient-intro-for-self"
          onClick={() => handleSelectOrderType("for_self")}
          className="group flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white p-6 text-center transition-all hover:border-dodger-blue-700 hover:bg-dodger-blue-50"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 text-gray-600 group-hover:bg-dodger-blue-100 group-hover:text-dodger-blue-700">
            <UserIcon size={28} />
          </div>
          <span className="font-medium text-foreground">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SELF")}</span>
        </button>
        <button
          type="button"
          data-testid="patient-intro-for-someone"
          onClick={() => handleSelectOrderType("for_someone")}
          className="group flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white p-6 text-center transition-all hover:border-dodger-blue-700 hover:bg-dodger-blue-50"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 text-gray-600 group-hover:bg-dodger-blue-100 group-hover:text-dodger-blue-700">
            <UsersIcon size={28} />
          </div>
          <span className="font-medium text-foreground">{t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.FOR_SOMEONE")}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className={cn(currentActiveCard === "customer" && "hidden")}>
        <EditableSummaryCard
          title={`1. ${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA")}`}
          description={
            t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA_DESCRIPTION") +
            (hasLabplus ? ` ${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_DATA_DESCRIPTION_LABPLUS")}` : "")
          }
          icon={<UserIcon size={20} />}
          status={currentActiveCard === "patient" ? "editing" : patientSnapshot ? "completed" : "empty"}
          summary={renderPatientSummary()}
          onEdit={() => handleOpenCard("patient")}
          validationMessage={validationFlash.patient ? validationBanner : undefined}
        >
          {/* The orderType field below is a RHF v6 Controller — its internal
              state only reacts to setValue once the Controller is mounted (the
              setter lives on fieldsRef[name].ref.value, populated at mount).
              We keep both layers always mounted and toggle visibility via CSS
              so the intro-click setValue lands on a live Controller. */}
          <div className={cn(!showPatientIntro && "hidden")}>{patientIntroView}</div>
          <div className={cn("flex flex-col gap-6", showPatientIntro && "hidden")}>
            {patientSection}
            <div className="flex justify-end gap-3 border-gray-100 border-t pt-4" id="patient-card-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onCancelPatient?.(patientSnapshotRef.current);
                  handleOpenCard(null);
                }}
              >
                Anuluj
              </Button>
              <Button type="button" onClick={handleCompletePatient} className="min-w-[200px]">
                Zapisz
              </Button>
            </div>
          </div>
        </EditableSummaryCard>
      </div>

      <div className={cn(currentActiveCard === "patient" && "hidden")}>
        <EditableSummaryCard
          title={`2. ${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.CUSTOMER_DATA")}`}
          description={t(`cmsTranslations.${configName}.user_data_form.description`)}
          icon={<UsersIcon size={20} />}
          status={currentActiveCard === "customer" ? "editing" : customerSnapshot ? "completed" : "empty"}
          summary={renderCustomerSummary()}
          onEdit={() => handleOpenCard("customer")}
          lockEdit={lockCustomerEdit}
          validationMessage={validationFlash.customer ? validationBanner : undefined}
        >
          <div className="flex flex-col gap-6">
            {customerSection}
            <div
              className={cn(
                "flex border-gray-100 border-t pt-4",
                // While phone auth is in progress, "Zapisz" doesn't have anything to save yet
                // (no customer fields/registration form on screen), so we drop it and center
                // the lone "Anuluj" so it doesn't sit awkwardly off to one side.
                isPhoneAuthInProgress ? "justify-center" : "justify-end gap-3",
              )}
              id="customer-card-actions"
            >
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onCancelCustomer?.(customerSnapshotRef.current);
                  handleOpenCard(null);
                }}
              >
                Anuluj
              </Button>
              {!isPhoneAuthInProgress && (
                <Button type="button" onClick={handleCompleteCustomer} className="min-w-[200px]">
                  Zapisz
                </Button>
              )}
            </div>
          </div>
        </EditableSummaryCard>
      </div>
    </div>
  );
};
